import React, { useState, useEffect, useMemo } from "react";
import { useGlobalState } from "../../contexts/GlobalContext";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { formatPhoneNumberInput } from '../../utils/formatters';
import { isRealtor } from '../../utils/userAccess';
import "../../Stylesheets/Form.css";
import { apiFetch } from "../../utils/api";
import Snackbar from "../Snackbar";

function NewContact({ userId }) {
  const { fetchContacts, state } = useGlobalState();
  const navigate = useNavigate();
  const generateContactId = () => uuidv4();
  const realtorView = isRealtor(state.user);
  const roleOptions = useMemo(
    () => (
      realtorView
        ? ["buyer", "seller", "investor", "prospect", "lead", "contact", "agent", "broker", "vendor", "other"]
        : ["prospect", "lead", "contact", "customer", "vendor", "other"]
    ),
    [realtorView]
  );

  const TIMELINE_TO_PURCHASE_OPTIONS = [
    "ASAP",
    "0-30 days",
    "30-60 days",
    "60-90 days",
    "3-6 months",
    "6-12 months",
    "12+ months",
    "Just researching",
  ];

  const PREAPPROVAL_STATUS_OPTIONS = [
    "Not started",
    "Lender shopping",
    "Pre-qualified",
    "Pre-approved",
    "Underwritten approval",
    "Cash buyer",
  ];

  const DECISION_ROLE_OPTIONS = [
    "Decision Maker",
    "Champion",
    "Influencer",
    "Evaluator",
    "End User",
    "Procurement",
  ];

  const URGENCY_LEVEL_OPTIONS = [
    "High",
    "Medium",
    "Low",
  ];

  const LEAD_SOURCE_OPTIONS = [
    { value: "referral", label: "Referral" },
    { value: "social_media", label: "Social Media" },
    { value: "advertisement", label: "Advertisement" },
    { value: "open_house", label: "Open House" },
    { value: "floor", label: "Floor" },
    { value: "cold call", label: "Cold Call" },
    { value: "other", label: "Other" },
  ];


  // Map property types to subtypes
  const subTypesByPropertyType = {
    Residential: [
      "Single-Family",
      "Condo",
      "Townhouse",
      "Duplex",
      "Triplex",
      "Quadraplex",
      "Multi-Family",
      "Apartment",
      "Co-op",
      "Mobile on Owned Land",
      "Manufactured Home",
      "Half Duplex/Villa",
      "Penthouse",
      "Loft",
      "Bungalow",
      "Cottage",
      "Patio Home",
      "Modular Home",
      "Student Housing",
      "Senior Living",
    ],
    Commercial: [
      "Retail",
      "Office",
      "Industrial",
      "Mixed-Use",
      "Medical Office",
      "Hospitality",
      "Restaurant",
      "Shopping Center",
      "Warehouse",
      "Self-Storage",
      "Data Center",
      "Flex Space",
      "Gas Station",
      "Car Wash",
      "Daycare Center",
      "Automotive",
      "Recreational",
      "Religious Facility",
    ],
    Land: [
      "Vacant",
      "Agricultural",
      "Ranch",
      "Development",
      "Timberland",
      "Recreational",
      "Waterfront",
      "Commercial Lot",
      "Residential Lot",
      "Industrial Lot",
      "Orchard/Vineyard",
      "Conservation",
      "Equestrian",
      "Mining",
      "Wetland",
    ],
    Other: [
      "Mobile - Rented Land",
      "Tiny Home",
      "Special Use",
      "Uncategorized",
      "Boat Slip",
      "Timeshare",
      "Parking Lot",
      "RV Park",
      "Cemetery",
      "Historical Property",
      "Live/Work Unit",
      "Floating Home",
      "Air Rights",
      "Seasonal Cabin",
      "Off-Grid Property",
      "Mixed-Use Other",
    ],
  };

  const STATE_OPTIONS = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" },
  ];


  // State for all form data
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    contact_id: "",
    best_time_to_contact: "",
    priority: "",
    lead_source: "",
    lead_source_detail: "",
    co_buyer_name: "",
    co_buyer_last_name: "",
    co_buyer_email: "",
    co_buyer_phone: "",
    prospect_stage: "",
    next_follow_up_at: "",
    pain_points: "",
    goals: "",
    motivation: "",
    preapproval_status: "",
    company_name: "",
    job_title: "",
    industry: "",
    decision_role: "",
    current_solution: "",
    urgency_level: "",
    next_step: "",
    estimated_value: "",
    owner_property_type: "",
    occupancy: "",
    current_property_street: "",
    current_property_city: "",
    current_property_state: "",
    current_property_zip: "",
    preferred_contact_method: "",
    notes: "",
    property_type: "",
    property_subtype: "",
    prospect_street: "",
    prospect_city: "",
    prospect_state: "",
    prospect_zip: "",
    parcelId: "",
    acreage: "",
    property_id: "", // Store the selected property_id
    home_features: "",
    financing_plan: "",
    down_payment: "",
    preferred_neighborhoods: "",
    budget: "",
    min_bedrooms: "",
    min_bathrooms: "",
    mail_street: "",
    mail_city: "",
    mail_state: "",
    mail_zip: "",
    timeline_to_purchase: "",
    role: "prospect",
    user_id: userId,
    llcs: "",
  });
  const [snackbar, setSnackbar] = useState({ show: false, message: "", type: "success" });

  const [extraFields, setExtraFields] = useState([]);
  const [properties, setProperties] = useState([]); // Store fetched properties for the datalist
  const [isPropertySelected, setIsPropertySelected] = useState(false); // Track if a property is selected
  const propertyRelatedToggleIds = [
    "current_property",
    "prospect_property",
    "home_features",
    "financing_plan",
    "preferred_neighborhoods",
  ];
  const leadSourceOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    LEAD_SOURCE_OPTIONS.forEach((option) => {
      seen.add(option.value.toLowerCase());
      options.push(option);
    });

    (state.contacts || []).forEach((contact) => {
      const detail = String(contact?.lead_source_detail || "").trim();
      if (!detail) return;

      const optionValue = `custom:${detail}`;
      const key = optionValue.toLowerCase();
      if (seen.has(key)) return;

      seen.add(key);
      options.push({ value: optionValue, label: detail });
    });

    return options;
  }, [state.contacts]);
  const leadSourceSelectValue =
    formData.lead_source === "other" && formData.lead_source_detail
      ? `custom:${formData.lead_source_detail}`
      : formData.lead_source;

  useEffect(() => {
    if (userId && (!state.contacts || state.contacts.length === 0)) {
      fetchContacts();
    }
  }, []);

  useEffect(() => {
    if (roleOptions.includes(formData.role)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      role: "prospect",
    }));
  }, [formData.role, roleOptions]);

  // Fetch properties when typing in the prospect_street field (with debounce)
  useEffect(() => {
    if (!realtorView) {
      setProperties([]);
      setIsPropertySelected(false);
      return;
    }

    const handler = setTimeout(() => {
      const fetchProperties = async () => {
        if (formData.prospect_street.length < 2) {
          setProperties([]);
          return;
        }

        try {
          const data = await apiFetch("fetchProperties", {
            queryParams: { search: formData.prospect_street },
          });

          if (data.success) {
            const mapped = data.properties.map((p) => ({
              id: p.propertyId || p.id,
              label: `${p.streetAddress}, ${p.city}, ${p.state} ${p.zipCode}`.trim(),
              ...p,
            }));
            setProperties(mapped);
          } else {
            console.error("Failed to fetch properties:", data.message);
            setProperties([]);
          }
        } catch (err) {
          console.error("Error fetching properties:", err);
          setProperties([]);
        }
      };

      fetchProperties();
    }, 300); // Debounce for 300ms

    return () => clearTimeout(handler);
  }, [formData.prospect_street, realtorView]);

  const availableExtraFields = [
    { id: "llcs", label: "LLC(s)" },
    { id: "co_buyer", label: "Co-Buyer" },
    { id: "prospect_status", label: "Prospect Status" },
    { id: "priority", label: "Priority" },
    { id: "sales_profile", label: "Sales Profile" },
    { id: "current_property", label: "Current Property Owned" },
    { id: "contact_method", label: "Preferred Contact Method" },
    { id: "investor", label: "Investor" },
    { id: "prospect_property", label: "Prospecting Property" },
    { id: "home_features", label: "Home Features" },
    { id: "financing_plan", label: "Financing Plan" },
    { id: "preferred_neighborhoods", label: "Preferred Neighborhood" },
    { id: "qualifications", label: "Qualifications" },
    { id: "mailing_address", label: "Mailing Address" },
  ].filter(({ id }) => realtorView || !propertyRelatedToggleIds.includes(id));

  // Handle changes to any input
  const capitalizeName = (text) => {
    if (!text) return "";
    return text.replace(/\b([a-z])/gi, (match) => match.toUpperCase());
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let newValue = value;

    if (name === "lead_source") {
      if (newValue.startsWith("custom:")) {
        setFormData((prev) => ({
          ...prev,
          lead_source: "other",
          lead_source_detail: newValue.slice("custom:".length),
        }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        lead_source: newValue,
        lead_source_detail: newValue === "other" ? prev.lead_source_detail : "",
      }));
      return;
    }

    if (name === "prospect_street") {
      // Reset property selection if the user edits the street address
      setIsPropertySelected(false);
      setFormData((prev) => ({
        ...prev,
        prospect_street: newValue,
        property_id: "",
        prospect_city: "",
        prospect_state: "",
        prospect_zip: "",
        parcelId: "",
        acreage: "",
        min_bedrooms: "",
        min_bathrooms: "",
        property_type: "",
        property_subtype: "",
      }));
      return;
    }

    if (name === "phone") {
      newValue = formatPhoneNumberInput(value);
    } else if (name === "first_name" || name === "last_name") {
      newValue = capitalizeName(value);
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };


  // Handle property selection from the datalist
  const handlePropertySelect = async (event) => {
    const selectedLabel = event.target.value;
    const selectedProperty = properties.find((prop) => prop.label === selectedLabel);

    if (!selectedProperty) {
      // Clear selection if no match is found
      setIsPropertySelected(false);
      setFormData((prev) => ({
        ...prev,
        property_id: "",
        prospect_street: selectedLabel, // Keep the typed value
        prospect_city: "",
        prospect_state: "",
        prospect_zip: "",
        parcelId: "",
        acreage: "",
        min_bedrooms: "",
        min_bathrooms: "",
        property_type: "",
        property_subtype: "",
      }));
      return;
    }

    const propertyId = selectedProperty.id;

    // Fetch full property details to fill in the form
    try {
      const data = await apiFetch("getPropertyDetails", {
        queryParams: { id: propertyId },
      });

      if (data.success) {
        const property = data.property;
        setFormData((prev) => ({
          ...prev,
          property_id: property.id,
          prospect_street: property.street_address,
          prospect_city: property.city,
          prospect_state: property.state,
          prospect_zip: property.zip_code,
          parcelId: property.parcel_number || "",
          acreage: property.acreage || "",
          min_bedrooms: property.bedrooms || "",
          min_bathrooms: property.bathrooms || "",
          property_type: property.property_type || "",
          property_subtype: property.property_subtype || "",
        }));
        setIsPropertySelected(true);
      } else {
        console.error("Failed to fetch property details:", data.message);
      }
    } catch (err) {
      console.error("Error fetching property details:", err);
    }
  };

  const clearToggleFields = (toggleId) => {
    switch (toggleId) {
      case "prospect_status":
        return { ...formData, prospect_stage: "", next_follow_up_at: "" };
      case "priority":
        return { ...formData, priority: "" };
      case "current_property":
        return {
          ...formData,
          owner_property_type: "",
          occupancy: "",
          current_property_street: "",
          current_property_city: "",
          current_property_state: "",
          current_property_zip: "",
        };
      case "contact_method":
        return { ...formData, preferred_contact_method: "" };
      case "notes":
        return { ...formData, notes: "" };
      case "co_buyer":
        return {
          ...formData,
          co_buyer_name: "",
          co_buyer_last_name: "",
          co_buyer_email: "",
          co_buyer_phone: "",
        };
      case "prospect_property":
        return {
          ...formData,
          property_type: "",
          property_subtype: "",
          prospect_street: "",
          prospect_city: "",
          prospect_state: "",
          prospect_zip: "",
          parcelId: "",
          acreage: "",
          min_bedrooms: "",
          min_bathrooms: "",
          property_id: "",
        };
      case "home_features":
        return { ...formData, home_features: "" };
      case "financing_plan":
        return { ...formData, financing_plan: "", down_payment: "" };
      case "preferred_neighborhoods":
        return { ...formData, preferred_neighborhoods: "" };
      case "qualifications":
        return {
          ...formData,
          pain_points: "",
          goals: "",
          budget: "",
          timeline_to_purchase: "",
          motivation: "",
          preapproval_status: "",
        };
      case "sales_profile":
        return {
          ...formData,
          company_name: "",
          job_title: "",
          industry: "",
          decision_role: "",
          current_solution: "",
          urgency_level: "",
          next_step: "",
          estimated_value: "",
        };
      case "bed_bath":
        return { ...formData, min_bedrooms: "", min_bathrooms: "" };
      case "mailing_address":
        return {
          ...formData,
          mail_street: "",
          mail_city: "",
          mail_state: "",
          mail_zip: "",
        };
      default:
        return formData;
    }
  };

  const handleExtraFieldToggle = (fieldId) => {
    if (extraFields.includes(fieldId)) {
      setExtraFields(extraFields.filter((f) => f !== fieldId));
      setFormData((prev) => {
        const cleared = clearToggleFields(fieldId);
        return fieldId === "investor" ? { ...cleared, role: "prospect" } : cleared;
      });
      setProperties([]);
      setIsPropertySelected(false);
    } else {
      setExtraFields([...extraFields, fieldId]);
      if (fieldId === "investor") {
        setFormData((prev) => ({ ...prev, role: "investor" }));
      }
    }
  };


  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();


    const payload = {
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      phone: formData.phone || null,
      email: formData.email || null,
      contact_id: generateContactId(),
      role: formData.role || "other",
      user_id: userId,
      mail_street: formData.mail_street || null,
      mail_city: formData.mail_city || null,
      mail_state: formData.mail_state || null,
      mail_zip: formData.mail_zip || null,
      priority: formData.priority || null,
      lead_source: formData.lead_source || null,
      lead_source_detail: formData.lead_source === "other" ? (formData.lead_source_detail || null) : null,
      co_buyer_name: formData.co_buyer_name || null,
      co_buyer_last_name: formData.co_buyer_last_name || null,
      co_buyer_email: formData.co_buyer_email || null,
      co_buyer_phone: formData.co_buyer_phone || null,
      prospect_stage: formData.prospect_stage || null,
      next_follow_up_at: formData.next_follow_up_at || null,
      company_name: formData.company_name || null,
      job_title: formData.job_title || null,
      industry: formData.industry || null,
      decision_role: formData.decision_role || null,
      current_solution: formData.current_solution || null,
      urgency_level: formData.urgency_level || null,
      next_step: formData.next_step || null,
      estimated_value: formData.estimated_value || null,
      pain_points: formData.pain_points || null,
      goals: formData.goals || null,
      motivation: formData.motivation || null,
      preapproval_status: formData.preapproval_status || null,
      owner_property_type: formData.owner_property_type || null,
      occupancy: formData.occupancy || null,
      current_property_street: formData.current_property_street || null,
      current_property_city: formData.current_property_city || null,
      current_property_state: formData.current_property_state || null,
      current_property_zip: formData.current_property_zip || null,
      preferred_contact_method: formData.preferred_contact_method || null,
      notes: formData.notes || null,
      property_type: formData.property_type || null,
      property_subtype: formData.property_subtype || null,
      prospect_street: formData.prospect_street || null,
      prospect_city: formData.prospect_city || "Unknown",
      prospect_state: formData.prospect_state || "Unknown",
      prospect_zip: formData.prospect_zip || "00000",
      parcel_number: formData.parcelId || null,
      acreage: formData.acreage || null,
      zoning: formData.property_type ? "Residential" : null,
      property_id: formData.property_id || null,
      home_features: formData.home_features || null,
      financing_plan: formData.financing_plan || null,
      down_payment: formData.down_payment || null,
      preferred_neighborhoods: formData.preferred_neighborhoods || null,
      budget: formData.budget || null,
      min_bedrooms: formData.min_bedrooms || null,
      min_bathrooms: formData.min_bathrooms || null,
      bedrooms: formData.min_bedrooms || null,
      bathrooms: formData.min_bathrooms || null,
      timeline_to_purchase: formData.timeline_to_purchase || null,
      time: formData.best_time_to_contact || null,
      additional_notes: formData.notes || null,
      seller_a: formData.co_buyer_name || null,
      seller_b: formData.co_buyer_last_name || null,
      llcs: formData.llcs || null,
      offer: null,
      due_diligence: null,
      days_for_closing: null,
      title_company: null,
      escrow_amount: null,
      term: null,
      financing_status: formData.financing_plan || null,
      wants_needs: formData.home_features || null,
      payment_type: null,
    };

    try {
      const data = await apiFetch("submitForm", {
        method: "POST",
        body: JSON.stringify(payload),
      });


      if (!data.success) throw new Error(data.message);

      const newContactId = data.contact_id;
      await fetchContacts();

      setSnackbar({ show: true, message: "✅ Prospect created successfully!", type: "success" });

      if (newContactId) {
        navigate(`/contact/${newContactId}`);
      } else {
        navigate("/contacts");
      }
    } catch (err) {
      console.error("❌ Submission Error:", err.message);
      setSnackbar({ show: true, message: `❌ Failed to submit: ${err.message}` , type: "danger"});
    }
  };

  const renderPropertyType = () => (
    <div className="row mb-2">
      <div className="col-md-3 mb-2">
        <label htmlFor="property_type" className="form-label">
          Property Type
        </label>
        <select
          className="form-select"
          id="property_type"
          name="property_type"
          value={formData.property_type}
          onChange={handleInputChange}
        >
          <option value="">Select Property Type</option>
          {Object.keys(subTypesByPropertyType).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
      {formData.property_type && subTypesByPropertyType[formData.property_type] && (
        <div className="col-md-3 mb-2">
          <label htmlFor="property_subtype" className="form-label">
            Property Subtype
          </label>
          <select
            className="form-select"
            id="property_subtype"
            name="property_subtype"
            value={formData.property_subtype}
            onChange={handleInputChange}
          >
            <option value="">Select Subtype</option>
            {subTypesByPropertyType[formData.property_type].map((sub) => (
              <option key={sub} value={sub}>
                {sub}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );

  const renderExtraSection = (fieldId) => {
    switch (fieldId) {
      case "llcs":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>LLC(s)</h5>
            <div className="col-md-6 mb-2">
              <label htmlFor="llcs" className="form-label">LLCs (comma-separated)</label>
              <input
                type="text"
                className="form-control"
                id="llcs"
                name="llcs"
                placeholder="Enter one or more LLCs, separated by commas"
                value={formData.llcs}
                onChange={handleInputChange}
              />
            </div>
          </div>
        );
      case "co_buyer":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Co-Buyer</h5>
            {[
              { name: "co_buyer_name", label: "Co-Buyer First Name" },
              { name: "co_buyer_last_name", label: "Co-Buyer Last Name" },
              { name: "co_buyer_email", label: "Co-Buyer Email", type: "email" },
              { name: "co_buyer_phone", label: "Co-Buyer Phone", type: "tel" },
            ].map(({ name, label, type = "text" }, idx) => (
              <div className="col-md-3 mb-2" key={idx}>
                <label htmlFor={name} className="form-label">
                  {label}
                </label>
                <input
                  type={type}
                  className="form-control"
                  id={name}
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                />
              </div>
            ))}
          </div>
        );
      case "prospect_status":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Prospect Status</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="prospect_stage" className="form-label">
                Stage
              </label>
              <select
                className="form-select"
                id="prospect_stage"
                name="prospect_stage"
                value={formData.prospect_stage}
                onChange={handleInputChange}
              >
                <option value="">Select a Status</option>
                <option value="New">New</option>
                <option value="Attempting Contact">Attempting Contact</option>
                <option value="Connected">Connected</option>
                <option value="Qualified">Qualified</option>
                <option value="Nurture">Nurture</option>
                <option value="Scheduled Follow-up">Scheduled Follow-up</option>
                <option value="Under Contract">Under Contract</option>
                <option value="Closed">Closed</option>
                <option value="Lost">Lost</option>
                <option value="Do Not Call">Do Not Call</option>
                <option value="Unreachable">Unreachable</option>
              </select>
            </div>
            <div className="col-md-4 mb-2">
              <label htmlFor="next_follow_up_at" className="form-label">
                Next Follow-Up
              </label>
              <input
                type="datetime-local"
                className="form-control"
                id="next_follow_up_at"
                name="next_follow_up_at"
                value={formData.next_follow_up_at}
                onChange={handleInputChange}
              />
            </div>
          </div>
        );
      case "priority":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Priority</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="priority" className="form-label">
                Priority
              </label>
              <select
                className="form-select"
                id="priority"
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>
        );
      case "sales_profile":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Sales Profile</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="company_name" className="form-label">
                Company Name
              </label>
              <input type="text" className="form-control" id="company_name" name="company_name" value={formData.company_name} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="job_title" className="form-label">
                Job Title
              </label>
              <input type="text" className="form-control" id="job_title" name="job_title" value={formData.job_title} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="industry" className="form-label">
                Industry
              </label>
              <input type="text" className="form-control" id="industry" name="industry" value={formData.industry} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="estimated_value" className="form-label">
                Estimated Value
              </label>
              <input type="number" className="form-control" id="estimated_value" name="estimated_value" value={formData.estimated_value} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="decision_role" className="form-label">
                Decision Role
              </label>
              <select className="form-select" id="decision_role" name="decision_role" value={formData.decision_role} onChange={handleInputChange}>
                <option value="">Select role</option>
                {DECISION_ROLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="urgency_level" className="form-label">
                Urgency Level
              </label>
              <select className="form-select" id="urgency_level" name="urgency_level" value={formData.urgency_level} onChange={handleInputChange}>
                <option value="">Select urgency</option>
                {URGENCY_LEVEL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-2">
              <label htmlFor="next_step" className="form-label">
                Next Step
              </label>
              <input type="text" className="form-control" id="next_step" name="next_step" value={formData.next_step} onChange={handleInputChange} />
            </div>
            <div className="col-md-12 mb-2">
              <label htmlFor="current_solution" className="form-label">
                Current Solution
              </label>
              <textarea className="form-control" id="current_solution" name="current_solution" rows="2" value={formData.current_solution} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "current_property":
        if (!realtorView) return null;
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Current Property Owned</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="owner_property_type" className="form-label">Property Type</label>
              <select className="form-select" id="owner_property_type" name="owner_property_type" value={formData.owner_property_type} onChange={handleInputChange}>
                <option value="">Select</option>
                <option value="Single-Family">Single-Family</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Condo">Condo</option>
                <option value="Land">Land</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="occupancy" className="form-label">Occupancy</label>
              <select className="form-select" id="occupancy" name="occupancy" value={formData.occupancy} onChange={handleInputChange}>
                <option value="">Select</option>
                <option value="Owner-occupied">Owner-occupied</option>
                <option value="Rental">Rental</option>
                <option value="Vacant">Vacant</option>
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="current_property_street" className="form-label">Street</label>
              <input type="text" className="form-control" id="current_property_street" name="current_property_street" value={formData.current_property_street} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="current_property_city" className="form-label">City</label>
              <input type="text" className="form-control" id="current_property_city" name="current_property_city" value={formData.current_property_city} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="current_property_state" className="form-label">State</label>
              <input type="text" className="form-control" id="current_property_state" name="current_property_state" list="contact-state-options" value={formData.current_property_state} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="current_property_zip" className="form-label">Zip</label>
              <input type="text" className="form-control" id="current_property_zip" name="current_property_zip" value={formData.current_property_zip} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "contact_method":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Preferred Contact Method</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="preferred_contact_method" className="form-label">Method</label>
              <select className="form-select" id="preferred_contact_method" name="preferred_contact_method" value={formData.preferred_contact_method} onChange={handleInputChange}>
                <option value="">Select</option>
                <option value="Phone">Phone</option>
                <option value="Email">Email</option>
                <option value="Text">Text</option>
              </select>
            </div>
          </div>
        );
      case "investor":
        return null;
      case "prospect_property":
        if (!realtorView) return null;
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Prospecting Property</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="prospect_street" className="form-label">Street</label>
              <input type="text" className="form-control" id="prospect_street" name="prospect_street" value={formData.prospect_street} onChange={handleInputChange} onInput={handlePropertySelect} list="property-suggestions" placeholder="Start typing to search..." />
              <datalist id="property-suggestions">
                {properties.map((property) => (
                  <option key={property.id} value={property.label} />
                ))}
              </datalist>
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="prospect_city" className="form-label">City</label>
              <input type="text" className="form-control" id="prospect_city" name="prospect_city" value={formData.prospect_city} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="prospect_state" className="form-label">State</label>
              <input type="text" className="form-control" id="prospect_state" name="prospect_state" list="contact-state-options" value={formData.prospect_state} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="prospect_zip" className="form-label">Zip</label>
              <input type="text" className="form-control" id="prospect_zip" name="prospect_zip" value={formData.prospect_zip} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="min_bedrooms" className="form-label">Bedrooms</label>
              <input type="text" className="form-control" id="min_bedrooms" name="min_bedrooms" value={formData.min_bedrooms} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="min_bathrooms" className="form-label">Bathrooms</label>
              <input type="text" className="form-control" id="min_bathrooms" name="min_bathrooms" value={formData.min_bathrooms} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="acreage" className="form-label">Acreage</label>
              <input type="text" className="form-control" id="acreage" name="acreage" value={formData.acreage} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="parcelId" className="form-label">Parcel ID</label>
              <input type="text" className="form-control" id="parcelId" name="parcelId" value={formData.parcelId} onChange={handleInputChange} />
            </div>
            {renderPropertyType()}
          </div>
        );
      case "home_features":
        if (!realtorView) return null;
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Home Features</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="home_features" className="form-label">Home Features</label>
              <input type="text" className="form-control" id="home_features" name="home_features" value={formData.home_features} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "financing_plan":
        if (!realtorView) return null;
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Financing Plan</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="financing_plan" className="form-label">Financing Plan</label>
              <select className="form-select" id="financing_plan" name="financing_plan" value={formData.financing_plan} onChange={handleInputChange}>
                <option value="">Select Financing Plan</option>
                <option value="Cash buyer">Cash Buyer</option>
                <option value="Pre-approved">Pre-Approved Loan</option>
                <option value="Not approved">Seeking Loan Approval</option>
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="down_payment" className="form-label">Down Payment</label>
              <input type="text" className="form-control" id="down_payment" name="down_payment" value={formData.down_payment} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "preferred_neighborhoods":
        if (!realtorView) return null;
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Preferred Neighborhood</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="preferred_neighborhoods" className="form-label">Neighborhood</label>
              <input type="text" className="form-control" id="preferred_neighborhoods" name="preferred_neighborhoods" value={formData.preferred_neighborhoods} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "qualifications":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Qualifications</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="budget" className="form-label">Budget</label>
              <input type="text" className="form-control" id="budget" name="budget" value={formData.budget} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="timeline_to_purchase" className="form-label">Timeline to Purchase</label>
              <select className="form-select" id="timeline_to_purchase" name="timeline_to_purchase" value={formData.timeline_to_purchase} onChange={handleInputChange}>
                <option value="">Select timeline</option>
                {TIMELINE_TO_PURCHASE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {realtorView && (
              <div className="col-md-3 mb-2">
                <label htmlFor="preapproval_status" className="form-label">Preapproval Status</label>
                <select className="form-select" id="preapproval_status" name="preapproval_status" value={formData.preapproval_status} onChange={handleInputChange}>
                  <option value="">Select status</option>
                  {PREAPPROVAL_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="col-md-3 mb-2">
              <label htmlFor="motivation" className="form-label">Motivation</label>
              <input type="text" className="form-control" id="motivation" name="motivation" value={formData.motivation} onChange={handleInputChange} />
            </div>
            <div className="col-md-6 mb-2">
              <label htmlFor="pain_points" className="form-label">Pain Points</label>
              <textarea className="form-control" id="pain_points" name="pain_points" rows="2" value={formData.pain_points} onChange={handleInputChange} />
            </div>
            <div className="col-md-6 mb-2">
              <label htmlFor="goals" className="form-label">Goals</label>
              <textarea className="form-control" id="goals" name="goals" rows="2" value={formData.goals} onChange={handleInputChange} />
            </div>
          </div>
        );
      case "mailing_address":
        return (
          <div className="row mb-2" key={fieldId}>
            <h5>Mailing Address</h5>
            <div className="col-md-3 mb-2">
              <label htmlFor="mail_street" className="form-label">Street</label>
              <input type="text" className="form-control" id="mail_street" name="mail_street" value={formData.mail_street} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="mail_city" className="form-label">City</label>
              <input type="text" className="form-control" id="mail_city" name="mail_city" value={formData.mail_city} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="mail_state" className="form-label">State</label>
              <input type="text" className="form-control" id="mail_state" name="mail_state" list="contact-state-options" value={formData.mail_state} onChange={handleInputChange} />
            </div>
            <div className="col-md-3 mb-2">
              <label htmlFor="mail_zip" className="form-label">Zip</label>
              <input type="text" className="form-control" id="mail_zip" name="mail_zip" value={formData.mail_zip} onChange={handleInputChange} />
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
    <Snackbar
      show={snackbar.show}
      message={snackbar.message}
      type={snackbar.type}
      onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
    />
    <form onSubmit={handleSubmit} className="container mt-2">
      <datalist id="contact-state-options">
        {STATE_OPTIONS.map(({ value, label }) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </datalist>
      {/* Basic Info (Always Visible) */}
      <div className="row mb-2">
        {[
          { name: "first_name", label: "First Name" },
          { name: "last_name", label: "Last Name" },
          { name: "email", label: "Email", type: "email" },
          { name: "phone", label: "Phone", type: "tel" },
        ].map(({ name, label, type = "text" }, idx) => (
          <div className="col-md-3 mb-2" key={idx}>
            <label htmlFor={name} className="form-label">
              {label}
            </label>
            <input
              type={type}
              className="form-control"
              id={name}
              name={name}
              value={formData[name]}
              onChange={handleInputChange}
            />
          </div>
        ))}
      </div>

      {/* Best Time & Source */}
      <div className="row mb-2">
        <div className="col-md-3 mb-2">
          <label htmlFor="role" className="form-label">
            Role
          </label>
          <select
            className="form-select"
            id="role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            {roleOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <label htmlFor="best_time_to_contact" className="form-label">
            Best Time to Contact
          </label>
          <select
            className="form-select"
            id="best_time_to_contact"
            name="best_time_to_contact"
            value={formData.best_time_to_contact}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            {["morning", "afternoon", "evening"].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <label htmlFor="lead_source" className="form-label">
            Prospect Source
          </label>
          <select
            className="form-select"
            id="lead_source"
            name="lead_source"
            value={leadSourceSelectValue}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            {leadSourceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {formData.lead_source === "other" && (
          <div className="col-md-6 mb-2">
            <label htmlFor="lead_source_detail" className="form-label">
              Source Detail
            </label>
            <input
              type="text"
              className="form-control"
              id="lead_source_detail"
              name="lead_source_detail"
              placeholder="Describe the source"
              value={formData.lead_source_detail}
              onChange={handleInputChange}
            />
          </div>
        )}
      </div>
      <div className="row mb-2">
        <h5>Notes</h5>
        <div className="col-md-12">
          <label htmlFor="notes" className="form-label">
            Call / Prospect Notes
          </label>
          <textarea
            className="form-control"
            id="notes"
            name="notes"
            rows="2"
            value={formData.notes}
            onChange={handleInputChange}
          />
        </div>
      </div>

     

      {availableExtraFields
        .filter(({ id }) => extraFields.includes(id))
        .map(({ id }) => renderExtraSection(id))}
       {/* Extra Fields Selector */}
      <div className="row mb-3">
        <label className="form-label">Add Extra Fields</label>
        <div className="row">
          {availableExtraFields.map(({ id, label }) => (
            <div className="col-md-3 mb-2" key={id}>
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={id}
                  checked={extraFields.includes(id)}
                  onChange={() => handleExtraFieldToggle(id)}
                />
                <label className="form-check-label ms-1" htmlFor={id}>
                  {label}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      <div className="row mb-2 text-center">
        <div className="col-md-12">
          <button type="submit" className="btn btn-primary w-50">
            Submit Prospect
          </button>
        </div>
      </div>
    </form>
    </>
  );
}

export default NewContact;
